import { KeyboardEvent, useEffect, useId, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Field,
  Fieldset,
  Heading,
  Input,
  Label,
  Paragraph,
  Skeleton,
} from '@digdir/designsystemet-react';
import { Routes } from '../../enums/Routes';
import { buildRoute } from '../../utils/routeBuilder';
import { ValidationUtils } from '../../utils/ValidationUtils';
import { useGetQuestionTrends } from '../../hooks/queries/useGetQuestionTrends';
import { TeamSelect } from '../../components/TeamSelect';
import { countSurveys, oneYearAgo, toLocalISODate } from './questionTrends.utils';
import { QuestionTrendCard, type ChartType } from './components/QuestionTrendCard';
import { PillToggle } from './components/PillToggle';
import classes from './QuestionTrends.module.css';

type View = 'all' | 'one';

/** Spørsmål over tid for et team (psykologisk trygghet) */
export const QuestionTrends = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const teamId = Number(useParams().teamId);
  const id = useId();
  const oneHeadingRef = useRef<HTMLHeadingElement>(null);
  const [fromDate, setFromDate] = useState(() => oneYearAgo());
  const [toDate, setToDate] = useState(() => toLocalISODate(new Date()));
  const [view, setView] = useState<View>('all');
  const [chartType, setChartType] = useState<ChartType>('bar');
  const [index, setIndex] = useState(0);
  const [focusOne, setFocusOne] = useState(false);

  const { hasError: hasPeriodError, data: periodError } = ValidationUtils.validateDatePickerForm({
    fromDate,
    toDate,
  });
  const { data, error, isLoading } = useGetQuestionTrends(fromDate, toDate, teamId, !hasPeriodError);
  const questionTrends = (data?.questionTrends ?? []).filter((questionTrend) => questionTrend.trends.length > 0);
  const current = Math.min(index, Math.max(0, questionTrends.length - 1));

  // «Se større» flytter fokus til spørsmålet som åpnes
  useEffect(() => {
    if (focusOne && view === 'one') {
      oneHeadingRef.current?.focus();
      setFocusOne(false);
    }
  }, [focusOne, view]);

  const openQuestion = (questionIndex: number) => {
    setIndex(questionIndex);
    setView('one');
    setFocusOne(true);
  };

  // Piltastene blar mellom spørsmålene, som før. I en fokusert graf flytter de mellom punktene.
  const handleArrows = (event: KeyboardEvent) => {
    if ((event.target as HTMLElement).closest('.recharts-wrapper')) return;
    if (event.key === 'ArrowLeft' && current > 0) setIndex(current - 1);
    if (event.key === 'ArrowRight' && current < questionTrends.length - 1) setIndex(current + 1);
  };

  const content = () => {
    if (hasPeriodError) {
      return (
        <Alert data-color="info">
          <Paragraph>{periodError}</Paragraph>
        </Alert>
      );
    }
    if (isLoading) {
      return (
        <div aria-busy="true" className={classes.grid}>
          {[1, 2, 3, 4].map((key) => (
            <Skeleton key={key} variant="rectangle" width="100%" height={320} />
          ))}
        </div>
      );
    }
    if (error) {
      return (
        <Alert data-color="danger">
          <Paragraph>{error.message}</Paragraph>
        </Alert>
      );
    }
    if (questionTrends.length === 0) {
      return (
        <div className={classes.empty}>
          <Heading level={2} data-size="xs">
            {t('dashboard.questionTrends.noTrendData')}
          </Heading>
          <Paragraph className={classes.subtle}>{t('dashboard.questionTrends.noTrendDataDescription')}</Paragraph>
        </div>
      );
    }
    if (view === 'one') {
      const questionTrend = questionTrends[current];
      return (
        <section aria-labelledby={`${id}-one`} className={classes.onePanel} onKeyDown={handleArrows}>
          <div className={classes.oneBody}>
            <QuestionTrendCard
              questionTrend={questionTrend}
              chartType={chartType}
              size="large"
              headingId={`${id}-one`}
              headingRef={oneHeadingRef}
              eyebrow={t('questionTrends.page.counter', { current: current + 1, total: questionTrends.length })}
            />
          </div>
          <div className={classes.oneFooter}>
            <Button variant="secondary" disabled={current === 0} onClick={() => setIndex(current - 1)}>
              {t('questionTrends.page.previous')}
            </Button>
            <Button disabled={current === questionTrends.length - 1} onClick={() => setIndex(current + 1)}>
              {t('questionTrends.page.next')}
            </Button>
          </div>
        </section>
      );
    }
    return (
      <ul className={classes.grid}>
        {questionTrends.map((questionTrend, questionIndex) => (
          <li key={questionTrend.question.id} className={classes.card}>
            <QuestionTrendCard
              questionTrend={questionTrend}
              chartType={chartType}
              size="small"
              headingId={`${id}-q-${questionTrend.question.id}`}
              footer={
                <button
                  type="button"
                  className={`ds-link ${classes.more}`}
                  aria-describedby={`${id}-q-${questionTrend.question.id}`}
                  onClick={() => openQuestion(questionIndex)}
                >
                  {t('questionTrends.page.seeLarger')}
                </button>
              }
            />
          </li>
        ))}
      </ul>
    );
  };

  return (
    <div className={classes.page}>
      <div aria-hidden="true" className={classes.backdrop} />
      <div className={classes.content}>
        <header className={classes.header}>
          <div className={classes.heading}>
            <span className={classes.eyebrow}>{t('questionTrends.page.eyebrow')}</span>
            <Heading level={1} data-size="xl">
              {t('questionTrends.page.title')}
            </Heading>
            <Paragraph className={classes.subtle}>{t('questionTrends.page.lead')}</Paragraph>
          </div>
          <div className={classes.filters}>
            <TeamSelect
              value={teamId}
              onChange={(newTeamId) => navigate(buildRoute(Routes.QuestionTrends, { teamId: newTeamId }))}
            />
            <Fieldset className={classes.period}>
              <Fieldset.Legend>{t('questionTrends.page.period')}</Fieldset.Legend>
              <div className={classes.dates}>
                <Field>
                  <Label htmlFor={`${id}-from`} className={classes.srOnly}>
                    {t('questionTrends.page.from')}
                  </Label>
                  <Input
                    id={`${id}-from`}
                    type="date"
                    value={fromDate}
                    max={toDate || undefined}
                    onChange={(event) => setFromDate(event.target.value)}
                  />
                </Field>
                <span aria-hidden="true" className={classes.subtle}>
                  –
                </span>
                <Field>
                  <Label htmlFor={`${id}-to`} className={classes.srOnly}>
                    {t('questionTrends.page.to')}
                  </Label>
                  <Input
                    id={`${id}-to`}
                    type="date"
                    value={toDate}
                    min={fromDate || undefined}
                    max={toLocalISODate(new Date())}
                    onChange={(event) => setToDate(event.target.value)}
                  />
                </Field>
              </div>
            </Fieldset>
          </div>
        </header>

        <div className={classes.toolbar}>
          <Paragraph data-size="sm" role="status" className={classes.subtle}>
            {!isLoading && !hasPeriodError && !error
              ? `${t('questionTrends.page.questionCount', { count: questionTrends.length })} · ${t(
                  'questionTrends.page.surveyCount',
                  { count: countSurveys(questionTrends) },
                )}`
              : ''}
          </Paragraph>
          <div className={classes.toggles}>
            <PillToggle
              label={t('questionTrends.page.view')}
              value={view}
              onChange={setView}
              options={[
                { value: 'all', label: t('dashboard.questionTrends.viewMode.all') },
                { value: 'one', label: t('dashboard.questionTrends.viewMode.oneByOne') },
              ]}
            />
            <PillToggle
              label={t('dashboard.questionTrends.selectChartType')}
              value={chartType}
              onChange={setChartType}
              options={[
                { value: 'bar', label: t('dashboard.questionTrends.chartType.bars') },
                { value: 'line', label: t('dashboard.questionTrends.chartType.lines') },
              ]}
            />
          </div>
        </div>

        {content()}

        <Paragraph data-size="sm" className={classes.subtle}>
          {t('questionTrends.page.legend')}
        </Paragraph>
      </div>
    </div>
  );
};
